// @flow
/* eslint-disable ta-code-conventions/has-matching-test,ta-code-conventions/has-matching-storydoc */

import * as React from 'react';
import { connect } from 'react-redux';
import { TravelInfo } from '@ta/lithium.context';
import { actions } from '@ta/common.state';
import { globalize } from '@ta/common.i18n';
import {
  parseDate,
  serializeDate,
  parseStayDates,
  parseGuestInfo,
} from '@ta/platform.travel-info-parsing';

type ExternalProps = {|
  +children: React.Node,
|};

type DispatchProps = {|
  +setRestaurantsDate: (Date, string) => void,
  +setHotelsTravelInfo: (?string, ?string) => void,
  +setAttractionsTravelInfo: (?string, ?string, ?string, ?number, ?number) => void,
  +setFlightsTravelInfo: (?string, ?string, ?string) => void,
|};

type StateProps = {|
  +attractionsTravelInfo: {|
    +singleDate: null | string,
    +fromDate: null | string,
    +toDate: null | string,
    +attractionPaxAdults: null | number,
    +attractionPaxChildren: null | number,
  |},
  +restaurantsDate: null | string,
  +hotelsTravelInfo: null | {|
    +stayDates: string,
    +guests: string,
    +defaultDates: boolean,
  |},
  +flightsTravelInfo: {|
    +oneWayDate: null | string,
    +departureDate: null | string,
    +returnDate: null | string,
  |},
|};

type Props = {|
  ...DispatchProps,
  ...StateProps,
  ...ExternalProps,
|};

const persist = (arg, value) =>
  fetch(`/UpdateSessionDatesAjax?${arg}=${value}`, { credentials: 'same-origin' });

const updater = (
  setAttractionsTravelInfo,
  setRestaurantsDate,
  setHotelsTravelInfo,
  setFlightsTravelInfo,
) => ({
  attractions: {
    setSingleDate: singleDate => {
      setAttractionsTravelInfo(serializeDate(singleDate, '-'));
      persist('attrdate', singleDate === null ? '_' : serializeDate(singleDate));
    },
    setDates: (fromDate, toDate) => {
      setAttractionsTravelInfo(undefined, serializeDate(fromDate, '-'), serializeDate(toDate, '-'));
      persist('attrrange', `${serializeDate(fromDate)}_${serializeDate(toDate)}`);
    },
    setPassengers: (adults, children) => {
      setAttractionsTravelInfo(undefined, undefined, undefined, adults, children);
      persist('zuf', `${adults}_${children}`);
    },
  },
  restaurants: {
    setDate: date => {
      if (date) {
        setRestaurantsDate(date, globalize.formatDate('week_date_short', date));
        persist('eaterydate', serializeDate(date));
      }
    },
  },
  hotels: {
    setStayDates: (checkInDate, checkOutDate) => {
      const newStayDates = `${serializeDate(checkInDate)}_${serializeDate(checkOutDate)}`;
      setHotelsTravelInfo(newStayDates);
      persist('staydates', newStayDates);
    },
    setGuestInfo: (adults, rooms, childAgesPerRoom) => {
      const newGuestInfo = `${rooms}_${adults}${
        childAgesPerRoom.length > 0
          ? `_${childAgesPerRoom.map(room => room.join(',')).join(':')}`
          : ''
      }`;
      setHotelsTravelInfo(undefined, newGuestInfo);
      persist('uguests', newGuestInfo);
    },
  },
  flights: {
    setOneWayDate: oneWayDate => {
      setFlightsTravelInfo(serializeDate(oneWayDate));
      persist('oneway', serializeDate(oneWayDate));
    },
    setRoundTripDates: (departureDate, returnDate) => {
      setFlightsTravelInfo(undefined, serializeDate(departureDate), serializeDate(returnDate));
      persist('rtdates', `${serializeDate(departureDate)}_${serializeDate(returnDate)}`);
    },
  },
});

const deserializeHotels = ({ stayDates, guests, defaultDates }) => {
  const parsedStayDates = parseStayDates(stayDates);
  const parsedGuestInfo = parseGuestInfo(guests);
  if (
    !parsedStayDates ||
    !parsedGuestInfo ||
    !parsedStayDates.checkInDate ||
    !parsedStayDates.checkOutDate
  ) {
    return null;
  }
  return {
    checkInDate: parsedStayDates.checkInDate,
    checkOutDate: parsedStayDates.checkOutDate,
    defaultDates,
    rooms: parsedGuestInfo.rooms,
    adults: parsedGuestInfo.adults,
    children: parsedGuestInfo.children,
    childAgesPerRoom: parsedGuestInfo.childAgesPerRoom,
  };
};

const deserialize = ({ hotels, restaurants, attractions, flights }) => ({
  attractions: {
    singleDate: parseDate(attractions.singleDate),
    fromDate: parseDate(attractions.fromDate),
    toDate: parseDate(attractions.toDate),
    adults: attractions.attractionPaxAdults,
    children: attractions.attractionPaxChildren,
  },
  restaurants: {
    date: parseDate(restaurants.date, '-'),
  },
  hotels: !hotels ? null : deserializeHotels(hotels),
  flights: {
    oneWayDate: parseDate(flights.oneWayDate, '-'),
    departureDate: parseDate(flights.departureDate, '-'),
    returnDate: parseDate(flights.returnDate, '-'),
  },
});

const TravelInfoProvider = ({
  attractionsTravelInfo,
  hotelsTravelInfo,
  restaurantsDate,
  flightsTravelInfo,
  setRestaurantsDate,
  setHotelsTravelInfo,
  setAttractionsTravelInfo,
  setFlightsTravelInfo,
  children,
}: Props) => {
  const update = React.useMemo(
    () =>
      updater(
        setAttractionsTravelInfo,
        setRestaurantsDate,
        setHotelsTravelInfo,
        setFlightsTravelInfo,
      ),
    [setAttractionsTravelInfo, setHotelsTravelInfo, setRestaurantsDate, setFlightsTravelInfo],
  );

  const ctx = React.useMemo(
    () => ({
      ...deserialize({
        attractions: attractionsTravelInfo || {},
        hotels: hotelsTravelInfo,
        restaurants: { date: restaurantsDate },
        flights: flightsTravelInfo || {},
      }),
      update,
    }),
    [attractionsTravelInfo, hotelsTravelInfo, restaurantsDate, flightsTravelInfo, update],
  );
  return <TravelInfo.Provider value={ctx}>{children}</TravelInfo.Provider>;
};

export default connect<Props, ExternalProps, StateProps, DispatchProps, _, _>(
  (state): StateProps => ({
    attractionsTravelInfo: state.travelerInfo.attractions,
    restaurantsDate: state.travelerInfo.restaurants && state.travelerInfo.restaurants.date,
    hotelsTravelInfo: state.travelerInfo.hotels,
    flightsTravelInfo: state.travelerInfo.flights,
  }),
  (dispatch): DispatchProps => ({
    setRestaurantsDate: (date, formatted) =>
      dispatch(actions.travelerInfo.setEatTravelerDate(date, formatted)),
    setHotelsTravelInfo: (stayDates, guestInfo) =>
      dispatch(actions.travelerInfo.setHotelsTravelerInfo(stayDates, guestInfo, false)),
    setAttractionsTravelInfo: (singleDate, fromDate, toDate, adults, children) =>
      dispatch({
        type: 'SET_ATTRACTIONS_TRAVELER_INFO',
        travelerInfo: {
          singleDate,
          fromDate,
          toDate,
          attractionPaxAdults: adults,
          attractionPaxChildren: children,
        },
      }),
    setFlightsTravelInfo: (oneWayDate, departureDate, returnDate) =>
      dispatch(actions.travelerInfo.setFlightsTravelerInfo(oneWayDate, departureDate, returnDate)),
  }),
)(TravelInfoProvider);
